import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { FieldsetCard } from '@/components/atoms/FieldsetCard';
import { ReadOnlyField } from '@/components/atoms/ReadOnlyField/ReadOnlyField';
import { SectionLayout } from '@/components/atoms/SectionLayout/SectionLayout';
import { Switch } from '@/components/ui/switch';
import useBrains from '@/hooks/useBrains';
import { useTrackFormState } from '@/hooks/useTrackFormState';
import { selectFeatureFlags } from '@/redux/featureFlag/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { Brain } from '@/shared/types/brain';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { CLEAR_DIRTY_VALUES, KEEP_DIRTY_VALUES } from '@/util/forms/resetOptions';
import { formatLanguageLabel } from '@/util/languageOptions';

const FORM_ID = 'languages-form';

interface LegacyLanguagesForm {
  match_user_language: boolean;
}

interface LegacyLanguagesFieldsetProps {
  brain: Brain;
}

/** The Languages card without the `speech_strategy` flag: the language and the match-language toggle. */
export const LegacyLanguagesFieldset = ({ brain }: LegacyLanguagesFieldsetProps) => {
  const { t } = useTranslation();
  const featureFlags = useSelector(selectFeatureFlags);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );
  const { patchBrainGuidelinesAsync } = useBrains(brain.brain_id);

  const form = useForm<LegacyLanguagesForm>({
    mode: 'onTouched',
    resetOptions: KEEP_DIRTY_VALUES,
    values: { match_user_language: brain.guidelines?.match_user_language ?? false },
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty, isSubmitting },
  } = form;
  useTrackFormState({ isDirty, formId: FORM_ID });

  // Patch only this guideline, so a stale copy can't overwrite the others
  const onSubmit = async (data: LegacyLanguagesForm) => {
    await patchBrainGuidelinesAsync({
      brain_id: brain.brain_id,
      match_user_language: data.match_user_language,
    });
    form.reset(data, CLEAR_DIRTY_VALUES);
  };

  return (
    <SectionLayout title={t('ai_agents.languages.title')}>
      <form onSubmit={handleSubmit(onSubmit)} id={FORM_ID}>
        <FieldsetCard
          isDirty={isDirty}
          isLoading={isSubmitting}
          disabled={!isDirty || !canWrite}
          saveButtonLabel={t('common.save')}
          formId={FORM_ID}
        >
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <ReadOnlyField
                label={t('ai_agents.languages.language')}
                value={formatLanguageLabel(brain.language)}
                className="space-y-2"
                labelClassName="text-foreground"
              />
              <span className="text-sm leading-5 text-muted-foreground">
                {t('ai_agents.languages.language_helper')}
              </span>
            </div>

            {featureFlags.nlp_language_detector && (
              <Controller
                name="match_user_language"
                control={control}
                render={({ field }) => (
                  <Switch
                    variant="choice-card"
                    switchPosition="right"
                    label={t('ai_agents.advanced_settings.match_user_language.title')}
                    description={t('ai_agents.advanced_settings.match_user_language.subtitle')}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={!canWrite}
                  />
                )}
              />
            )}
          </div>
        </FieldsetCard>
      </form>
    </SectionLayout>
  );
};
